import { useCallback, useEffect, useRef, useState } from 'react';
import { apiMultipart } from '../api/client';

export type VoiceInputState = 'idle' | 'recording' | 'uploading' | 'error';

interface TranscribeResponse {
  transcript: string;
  model: string;
}

// Module-scoped global lock — only one MicButton may be active at a time
// across the whole app. Changes broadcast via a window event so any hook
// instance can re-render its "disabled because sibling is busy" state.
let micBusy = false;
const MIC_LOCK_EVENT = 'mic-lock-changed';

function setMicBusy(next: boolean): void {
  if (micBusy === next) return;
  micBusy = next;
  window.dispatchEvent(new Event(MIC_LOCK_EVENT));
}

function pickMimeType(): string {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4',
  ];
  const MR = (window as unknown as { MediaRecorder?: typeof MediaRecorder })
    .MediaRecorder;
  if (MR && typeof MR.isTypeSupported === 'function') {
    for (const c of candidates) {
      if (MR.isTypeSupported(c)) return c;
    }
  }
  return '';
}

export interface UseVoiceInputResult {
  state: VoiceInputState;
  start: () => Promise<void>;
  stop: () => Promise<void>;
  cancel: () => void;
  transcript: string | null;
  error: string | null;
  otherMicActive: boolean;
}

export function useVoiceInput(): UseVoiceInputResult {
  const [state, setState] = useState<VoiceInputState>('idle');
  const [transcript, setTranscript] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [otherMicActive, setOtherMicActive] = useState(false);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const ownsLockRef = useRef(false);

  useEffect(() => {
    const onChange = (): void => setOtherMicActive(micBusy && !ownsLockRef.current);
    window.addEventListener(MIC_LOCK_EVENT, onChange);
    return (): void => window.removeEventListener(MIC_LOCK_EVENT, onChange);
  }, []);

  const cleanupStream = useCallback((): void => {
    const stream = streamRef.current;
    if (stream) {
      for (const track of stream.getTracks()) track.stop();
    }
    streamRef.current = null;
    recorderRef.current = null;
    chunksRef.current = [];
  }, []);

  const releaseLock = useCallback((): void => {
    if (ownsLockRef.current) {
      ownsLockRef.current = false;
      setMicBusy(false);
    }
  }, []);

  const start = useCallback(async (): Promise<void> => {
    setError(null);
    setTranscript(null);
    if (micBusy) {
      setError('Another mic is already recording.');
      throw new Error('mic busy');
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Microphone not available in this browser.');
      setState('error');
      throw new Error('no getUserMedia');
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(`Microphone permission denied: ${msg}`);
      setState('error');
      throw err;
    }

    setMicBusy(true);
    ownsLockRef.current = true;

    const mimeType = pickMimeType();
    const rec = mimeType
      ? new MediaRecorder(stream, { mimeType })
      : new MediaRecorder(stream);
    recorderRef.current = rec;
    streamRef.current = stream;
    chunksRef.current = [];

    rec.addEventListener('dataavailable', (ev: BlobEvent) => {
      if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
    });
    rec.start();
    setState('recording');
  }, []);

  const stop = useCallback(async (): Promise<void> => {
    const rec = recorderRef.current;
    if (!rec || rec.state === 'inactive') {
      cleanupStream();
      releaseLock();
      setState('idle');
      return;
    }

    const finished: Promise<void> = new Promise((resolve) => {
      rec.addEventListener('stop', () => resolve(), { once: true });
    });
    rec.stop();
    await finished;

    const parts = chunksRef.current;
    const mime = rec.mimeType || 'audio/webm';
    const blob = new Blob(parts, { type: mime });
    cleanupStream();
    setState('uploading');

    try {
      const fd = new FormData();
      // Gemini Files API is happy with audio/webm uploads as-is; we just
      // forward whatever MediaRecorder produced with an appropriate filename.
      const ext = mime.includes('webm')
        ? 'webm'
        : mime.includes('ogg')
          ? 'ogg'
          : mime.includes('mp4')
            ? 'm4a'
            : 'bin';
      fd.append('audio', blob, `voice-recording.${ext}`);
      const res = await apiMultipart<TranscribeResponse>(
        '/api/voice/transcribe',
        fd,
      );
      const t = (res.transcript || '').trim();
      setTranscript(t);
      setState('idle');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      setState('error');
    } finally {
      releaseLock();
    }
  }, [cleanupStream, releaseLock]);

  const cancel = useCallback((): void => {
    try {
      const rec = recorderRef.current;
      if (rec && rec.state !== 'inactive') rec.stop();
    } catch {
      /* ignore */
    }
    cleanupStream();
    releaseLock();
    setState('idle');
  }, [cleanupStream, releaseLock]);

  useEffect(() => {
    return (): void => {
      cleanupStream();
      releaseLock();
    };
  }, [cleanupStream, releaseLock]);

  return { state, start, stop, cancel, transcript, error, otherMicActive };
}
